import { el, clear } from "./dom";
import { addDays, formatMinutes, weekStart } from "../shared/present";
import { zonedParts } from "../shared/slots";

export interface DatePicker {
  root: HTMLElement;
  getDates(): string[];
  setDates(dates: string[]): void;
  setTimezone(tz: string): void;
}

export function mountDatePicker(tz: string, initial: string[], onChange?: () => void): DatePicker {
  let timezone = tz;
  const selected = new Set(initial);
  const root = el("div", { class: "datepicker" });
  const cal = el("div", { class: "cal" });
  const counter = el("p", { class: "muted", "data-testid": "date-counter" });
  const refuse = el("p", { class: "error" });
  root.append(cal, counter, refuse);

  function today(): string {
    return zonedParts(Math.floor(Date.now() / 60_000), timezone).date;
  }

  function paint(): void {
    clear(cal);
    for (const d of ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]) {
      cal.append(el("div", { class: "dow label", text: d }));
    }
    const start = weekStart(today());
    const now = today();
    for (let i = 0; i < 56; i++) {
      const iso = addDays(start, i);
      const on = selected.has(iso);
      const past = iso < now;
      const b = el("button", {
        class: "day",
        type: "button",
        text: iso.slice(8),
        "data-date": iso,
        "aria-pressed": on ? "true" : "false",
      });
      if (past && !on) b.disabled = true;
      b.addEventListener("click", () => {
        refuse.textContent = "";
        if (selected.has(iso)) selected.delete(iso);
        else {
          if (selected.size >= 31) {
            refuse.textContent = "You can pick at most 31 days.";
            return;
          }
          selected.add(iso);
        }
        paint();
        onChange?.();
      });
      cal.append(b);
    }
    counter.textContent = `${selected.size} of 31 days`;
  }

  paint();
  return {
    root,
    getDates() {
      return [...selected].sort();
    },
    setDates(dates: string[]) {
      const next = [...dates].sort().join(",");
      if ([...selected].sort().join(",") === next) return;
      selected.clear();
      for (const d of dates) selected.add(d);
      paint();
    },
    setTimezone(next: string) {
      timezone = next;
      const now = today();
      let dropped = false;
      for (const d of [...selected]) {
        if (d < now) {
          selected.delete(d);
          dropped = true;
        }
      }
      paint();
      if (dropped) onChange?.();
    },
  };
}

export function fillTimeSelect(sel: HTMLSelectElement, value: number, end: boolean): void {
  clear(sel);
  const max = end ? 1440 : 1410;
  for (let m = 0; m <= max; m += 30) {
    const opt = el("option", {
      value: String(m),
      text: formatMinutes(m % 1440) + (m === 1440 ? " (next day)" : ""),
    });
    if (m === value) opt.selected = true;
    sel.append(opt);
  }
}

export function fillDurationSelect(sel: HTMLSelectElement, value: number): void {
  clear(sel);
  for (let m = 30; m <= 480; m += 30) {
    const opt = el("option", { value: String(m) });
    opt.textContent = m === 30 ? "30 min" : `${m / 60} hr`;
    if (m === value) opt.selected = true;
    sel.append(opt);
  }
}

export function labeled(text: string, control: HTMLElement): HTMLLabelElement {
  return el("label", { class: "field" }, el("span", { class: "label", text }), control);
}
